import { apiClient } from '@shared/api/client';
import { ApiResponse } from '@shared/api/api.types';

// Unified DTOs for user invitation with role assignment
export interface InviteUserWithRoleDTO {
  email: string;
  name?: string;
  role: string; // UserRole enum value
  tenantId: string;
  domainId?: string; // Required for FACULTY_ADMIN
  subjectId?: string; // Required for LECTURER, COURSE_REPRESENTATIVE
  assignedBy: string;
  expiresAt?: string;
}

export interface InvitationWithRoleResponseDTO {
  success: boolean;
  message: string;
  errorMessage?: string;
  userCreated: boolean;
  cognitoInvitationSent: boolean;
  roleAssigned: boolean;
  user: {
    id: string;
    tenantId: string;
    email: string;
    name: string;
    cognitoId?: string;
    status: 'invited' | 'active' | 'inactive';
    createdAt: string;
    updatedAt?: string;
  };
  userRole: any; // UserRoleDTO from user-role feature
  temporaryPassword?: string; // Only returned in development/testing environments
}

/**
 * Unified User Invitation API
 *
 * This replaces all scattered invitation methods across different features.
 * Handles both new user creation + Cognito invitation and existing user role assignment.
 */
export const userInvitationApi = {
  /**
   * Invite user with role assignment in a single operation
   *
   * For new users:
   * - Creates Cognito invitation
   * - Creates user record in database
   * - Assigns specified role
   *
   * For existing users:
   * - Skips Cognito invitation
   * - Assigns additional role to existing user
   */
  async inviteUserWithRole(
    data: InviteUserWithRoleDTO
  ): Promise<ApiResponse<InvitationWithRoleResponseDTO>> {
    return apiClient.post<InvitationWithRoleResponseDTO>('/users/invite-with-role', data);
  },

  /**
   * Resend invitation to an existing invited user
   *
   * This will:
   * - Generate a new temporary password
   * - Reset the user's password in Cognito
   * - Send a new invitation email with the new password
   *
   * Only works for users with status 'invited' who haven't logged in yet
   */
  async resendInvitation(email: string): Promise<ApiResponse<InvitationWithRoleResponseDTO>> {
    return apiClient.post<InvitationWithRoleResponseDTO>('/users/resend-invitation', { email });
  },
};
